/**
 * La frise du chapitre — chantier « manuel vivant », Φ-MV-5 (MV-TUT-02,
 * MV-TUT-13). Une case par notion, dans l'ordre d'enseignement : ce que la
 * classe a vu, ce qu'elle voit AUJOURD'HUI, ce qui vient.
 *
 * ─── Ce que la largeur d'une case dit ───────────────────────────────────────
 *
 * Le nombre de JOURS que le calendrier de l'objectif donne à la notion
 * (`du` → `au`, d'après le rythme du chapitre). C'est une durée de classe, pas
 * une mesure de l'élève : aucune case ne se « remplit ». L'état d'une case est
 * une position dans le calendrier (`vue`, `ici`, `a_venir`), la mention
 * « Acquis » est un palier, en mot.
 *
 * Une liste (`<ol>`), pas un dessin : chaque case se lit au lecteur d'écran
 * avec son rang, son titre, ses dates et son état. À l'étroit, les cases
 * passent à la ligne ; la page ne défile jamais de côté.
 */
import type { CaseDeFriseDto } from '@silex/shared';

import { jourCivil, joursCompris, rangCourt } from '../lib/manuel-tuteur-format';

const ETATS: Readonly<Record<CaseDeFriseDto['etat'], string>> = {
  vue: 'Vue en classe',
  ici: 'En classe aujourd’hui',
  a_venir: 'À venir',
};

export function FriseChapitre({
  cases,
  rangChapitre,
}: {
  readonly cases: readonly CaseDeFriseDto[];
  readonly rangChapitre: number;
}) {
  if (cases.length === 0) {
    return <p className="note-line">Sans dates, la frise du chapitre ne peut pas se dessiner.</p>;
  }
  return (
    <ol className="frise" data-testid="manuel-frise">
      {cases.map((c) => (
        <li
          key={c.notion.code}
          className={`frise__case frise__case--${c.etat}`}
          style={{ flexGrow: joursCompris(c.du, c.au) }}
        >
          <span className="frise__rang">
            {rangCourt(c.notion, rangChapitre)}
            {c.acquise ? <span className="frise__acquise"> · Acquis</span> : null}
          </span>
          <span className="frise__titre">{c.notion.titre}</span>
          <span className="frise__dates">
            {jourCivil(c.du)} → {jourCivil(c.au)} · {ETATS[c.etat]}
          </span>
        </li>
      ))}
    </ol>
  );
}
